import { decodeString } from "@findemdev/findem-helpers";
import type { IExecProject, IPipelineAgent } from "../../types/agent-pipelines";
import type { IGetCompanyLogosResponseDataObj } from "../../types/pub";
import {
  CURRENT_USER_COMPANY_META_REF_KEYS,
  MAX_VISIBLE_PAGE_BUTTONS,
  PAGINATION_ELLIPSIS,
  PROJECT_TABLE_COLUMN_DEFINITIONS,
  PROJECT_TABLE_SCROLL_GUTTER,
  PROJECT_CLIENT_COMPANY_META_REF_KEYS,
  SUBMITTED_STATUS_VALUES,
} from "./constants";
import { getRaasPortalDefinition } from "./portalConfig";
import {
  getRaasProjectListStatusView,
  RaasProjectListBackendStatus,
  RaasStatusAudience,
} from "./status";
import { isRaasToolAgentKey } from "./tools";
import {
  type IStatusView,
  type ProjectTableColumnKey,
  RaasPortalId,
} from "./types";
import type { IMilestoneItem } from "./components/MilestonesPane/types";

export const getPrimaryCompanyName = (name: string): string => {
  const match = name.match(/^([^[]+)/);
  return decodeString(match ? match[1].trim() : name) || name;
};

export const getSafeErrorMessage = ({ error }: { error: unknown }): string => {
  if (typeof error === "object" && error !== null) {
    const data = (error as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }

  return "An unexpected error occurred.";
};

export const getRaasApiErrorCode = ({
  error,
}: {
  error: unknown;
}): string | undefined => {
  if (typeof error !== "object" || error === null) return undefined;

  const data = (error as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return undefined;

  const code = (data as { code?: unknown; error_code?: unknown }).code;
  if (typeof code === "string") return code;

  const errorCode = (data as { error_code?: unknown }).error_code;
  return typeof errorCode === "string" ? errorCode : undefined;
};

export const isRaasInsufficientPermissionsError = ({
  error,
}: {
  error: unknown;
}): boolean => getRaasApiErrorCode({ error }) === "InternalServerError";

const CLIENT_VISIBLE_MILESTONE_STATUSES = new Set([
  "approved",
  "client_approved",
  "submitted_to_client",
]);

export const isClientVisibleMilestone = ({
  milestone,
}: {
  milestone?: IMilestoneItem;
}): boolean =>
  Boolean(
    milestone?.complete ||
      (milestone?.apiStatus &&
        CLIENT_VISIBLE_MILESTONE_STATUSES.has(milestone.apiStatus)),
  );

export const getRaasPortalBasePath = ({
  pathname,
}: {
  pathname: string;
}): string => {
  const projectsPathIndex = pathname.indexOf("/projects/");

  if (projectsPathIndex === -1) {
    return getRaasPortalDefinition({ pathname }).basePath;
  }

  return pathname.slice(0, projectsPathIndex);
};

export const isRaasInternalPortalPath = ({
  pathname,
}: {
  pathname: string;
}): boolean =>
  getRaasPortalDefinition({ pathname }).id === RaasPortalId.Internal;

export const getProjectTableScrollWidth = ({
  columnKeys,
}: {
  columnKeys: readonly ProjectTableColumnKey[];
}): number =>
  columnKeys.reduce(
    (scrollWidth, columnKey) =>
      scrollWidth + PROJECT_TABLE_COLUMN_DEFINITIONS[columnKey].width,
    PROJECT_TABLE_SCROLL_GUTTER,
  );

export const isRecord = ({
  value,
}: {
  value: unknown;
}): boolean =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getRecordValue = ({
  value,
}: {
  value: unknown;
}): Record<string, unknown> | undefined =>
  isRecord({ value }) ? (value as Record<string, unknown>) : undefined;

export const getStringValue = ({
  value,
}: {
  value: unknown;
}): string | undefined => {
  if (typeof value !== "string") return undefined;

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

export const getStringRecordValue = ({
  keys,
  record,
}: {
  keys: readonly string[];
  record: unknown;
}): string | undefined => {
  const valuesByKey = getRecordValue({ value: record });
  if (!valuesByKey) return undefined;

  for (const key of keys) {
    const value = getStringValue({ value: valuesByKey[key] });
    if (value) return value;
  }

  return undefined;
};

export const getProjectClientCompanyMetaRef = ({
  project,
}: {
  project: IExecProject;
}): string | undefined =>
  getStringRecordValue({
    keys: PROJECT_CLIENT_COMPANY_META_REF_KEYS,
    record: project,
  }) || getStringValue({ value: project.account_id });

export const getClientCompanyMetaRef = ({
  currentUser,
  project,
}: {
  currentUser: unknown;
  project: IExecProject;
}): string | undefined =>
  getStringRecordValue({
    keys: PROJECT_CLIENT_COMPANY_META_REF_KEYS,
    record: project,
  }) ||
  getStringRecordValue({
    keys: CURRENT_USER_COMPANY_META_REF_KEYS,
    record: currentUser,
  }) ||
  getStringValue({ value: project.account_id });

export const getLogoRef = ({
  companyDetails,
}: {
  companyDetails?: IGetCompanyLogosResponseDataObj;
}): string | undefined => {
  const logos = companyDetails?.company?.logos;
  if (!logos?.length) return undefined;

  const normalizeLogoRef = (value: unknown): string | undefined =>
    getStringValue({ value })?.toLowerCase();

  return (
    normalizeLogoRef(logos.find((logo) => logo.source === "manual")?.ref) ||
    normalizeLogoRef(logos.find((logo) => logo.source === "linkedin")?.ref)
  );
};

export const normalizeStatusValue = ({ value }: { value: unknown }): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const formatAgentName = ({ value }: { value: string }): string =>
  value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");

export enum RaasArtifactDisplayLabel {
  SearchBrief = "Search Brief",
  StructuredIntake = "Structured Intake",
}

const RAAS_ARTIFACT_DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  [RaasArtifactDisplayLabel.StructuredIntake]:
    RaasArtifactDisplayLabel.SearchBrief,
};

export const getRaasArtifactDisplayLabel = ({
  label,
}: {
  label?: string;
}): string => {
  const trimmedLabel = label?.trim();
  if (!trimmedLabel) return "";

  return RAAS_ARTIFACT_DISPLAY_LABEL_OVERRIDES[trimmedLabel] || trimmedLabel;
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";

  return "Good evening";
};

export const getFirstName = ({ name }: { name?: string }): string => {
  const [firstName] = decodeString(name || "").trim().split(/\s+/);
  return firstName || "there";
};

export const getTimestampMs = ({
  timestamp,
}: {
  timestamp?: number;
}): number | undefined => {
  if (!timestamp) return undefined;

  return timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
};

export const formatRelativeTime = ({
  timestamp,
}: {
  timestamp?: number;
}): string => {
  const timestampMs = getTimestampMs({ timestamp });
  if (!timestampMs) return "-";

  const deltaMs = Math.max(0, Date.now() - timestampMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;

  if (deltaMs < minute) return "Just now";
  if (deltaMs < hour) {
    const value = Math.floor(deltaMs / minute);
    return `${value} min${value === 1 ? "" : "s"} ago`;
  }
  if (deltaMs < day) {
    const value = Math.floor(deltaMs / hour);
    return `${value} hour${value === 1 ? "" : "s"} ago`;
  }
  if (deltaMs < month) {
    const value = Math.floor(deltaMs / day);
    return `${value} day${value === 1 ? "" : "s"} ago`;
  }

  const value = Math.floor(deltaMs / month);
  return `${value} month${value === 1 ? "" : "s"} ago`;
};

export const formatDateTitle = ({
  timestamp,
}: {
  timestamp?: number;
}): string => {
  const timestampMs = getTimestampMs({ timestamp });
  if (!timestampMs) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestampMs));
};

export const normalizeDomain = ({
  companyName,
  domain,
}: {
  companyName: string;
  domain: string;
}): string => {
  const trimmedDomain = domain.trim();
  if (trimmedDomain) return trimmedDomain;

  return `${companyName.trim().toLowerCase().replace(/\s+/g, "")}.com`;
};

export const getDeliverableAgents = ({
  pipelineAgents,
  project,
}: {
  pipelineAgents: IPipelineAgent[];
  project: IExecProject;
}): Array<{ key: string; label: string }> => {
  if (pipelineAgents.length > 0) {
    return pipelineAgents
      .filter((agent) => !isRaasToolAgentKey({ key: agent.agent_name }))
      .map((agent) => ({
        key: agent.agent_name,
        label: getRaasArtifactDisplayLabel({
          label:
            agent.display_name || formatAgentName({ value: agent.agent_name }),
        }),
      }));
  }

  const keys = new Set([
    ...Object.keys(project.session_ids || {}),
    ...Object.keys(project.agent_data || {}),
  ]);

  return Array.from(keys)
    .filter(
      (key) =>
        !["pipeline", "pipeline_count"].includes(key) &&
        !isRaasToolAgentKey({ key }),
    )
    .map((key) => ({
      key,
      label: getRaasArtifactDisplayLabel({
        label: formatAgentName({ value: key }),
      }),
    }));
};

export const getAgentRecord = ({
  agentKey,
  project,
}: {
  agentKey: string;
  project: IExecProject;
}): Record<string, unknown> | undefined => {
  const value = project.agent_data?.[agentKey];
  return getRecordValue({ value });
};

export const isAgentComplete = ({
  agentKey,
  project,
}: {
  agentKey: string;
  project: IExecProject;
}): boolean => getAgentRecord({ agentKey, project })?.complete === true;

export const isAgentStarted = ({
  agentKey,
  project,
}: {
  agentKey: string;
  project: IExecProject;
}): boolean =>
  Boolean(project.session_ids?.[agentKey]) ||
  project.agent_data?.[agentKey] !== undefined;

export const hasSubmittedToClient = ({
  project,
}: {
  project: IExecProject;
}): boolean =>
  Object.values(project.agent_data || {}).some((value) => {
    const submittedRecord = getRecordValue({ value });
    if (!submittedRecord) return false;
    if (
      submittedRecord.submitted_to_client === true ||
      submittedRecord.submittedToClient === true
    ) {
      return true;
    }

    return (
      SUBMITTED_STATUS_VALUES.has(
        normalizeStatusValue({ value: submittedRecord.status }),
      ) ||
      SUBMITTED_STATUS_VALUES.has(
        normalizeStatusValue({ value: submittedRecord.stage }),
      ) ||
      SUBMITTED_STATUS_VALUES.has(
        normalizeStatusValue({ value: submittedRecord.state }),
      )
    );
  });

export const getProjectStatus = ({
  audience = RaasStatusAudience.Internal,
  pipelineAgents,
  project,
}: {
  audience?: RaasStatusAudience;
  pipelineAgents: IPipelineAgent[];
  project: IExecProject;
}): IStatusView => {
  const agents = getDeliverableAgents({ pipelineAgents, project });
  const total = agents.length;
  const completed = agents.filter((agent) =>
    isAgentComplete({ agentKey: agent.key, project }),
  ).length;
  const started = agents.filter((agent) =>
    isAgentStarted({ agentKey: agent.key, project }),
  ).length;

  if (total > 0 && completed === total) {
    return getRaasProjectListStatusView({
      audience,
      value: RaasProjectListBackendStatus.Completed,
    });
  }
  if (hasSubmittedToClient({ project })) {
    return getRaasProjectListStatusView({
      audience,
      value: RaasProjectListBackendStatus.SubmittedToClient,
    });
  }
  if (started > 0) {
    return getRaasProjectListStatusView({
      audience,
      value: RaasProjectListBackendStatus.InProgress,
    });
  }

  return getRaasProjectListStatusView({
    audience,
    value: RaasProjectListBackendStatus.NotStarted,
  });
};

export const getMilestoneLabel = ({
  pipelineAgents,
  project,
}: {
  pipelineAgents: IPipelineAgent[];
  project: IExecProject;
}): string => {
  const agents = getDeliverableAgents({ pipelineAgents, project });
  if (agents.length === 0) return "-";

  const activeAgent = agents.find(
    (agent) =>
      isAgentStarted({ agentKey: agent.key, project }) &&
      !isAgentComplete({ agentKey: agent.key, project }),
  );
  if (activeAgent) return activeAgent.label;

  const nextAgent = agents.find(
    (agent) => !isAgentComplete({ agentKey: agent.key, project }),
  );
  if (nextAgent) return nextAgent.label;

  return agents[agents.length - 1]?.label || "Completed";
};

export const getPipelineLabel = ({
  project,
}: {
  project: IExecProject;
}): string => {
  const agentData = project.agent_data || {};
  const directValue = agentData.pipeline_count ?? agentData.pipeline;

  if (typeof directValue === "number" || typeof directValue === "string") {
    return String(directValue);
  }

  const analytics = agentData.analytics;
  const analyticsRecord = getRecordValue({ value: analytics });
  if (analyticsRecord) {
    const value = analyticsRecord.pipeline_count ?? analyticsRecord.pipeline;
    if (typeof value === "number" || typeof value === "string") {
      return String(value);
    }
  }

  return "—";
};

export const getVisiblePages = ({
  currentPage,
  totalPages,
}: {
  currentPage: number;
  totalPages: number;
}): number[] => {
  if (totalPages <= MAX_VISIBLE_PAGE_BUTTONS) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: number[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    pages.push(PAGINATION_ELLIPSIS);
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < totalPages - 1) {
    pages.push(PAGINATION_ELLIPSIS);
  }

  pages.push(totalPages);
  return pages;
};
